import { useQuery } from '@sequencehq/api'
import { dashboard20240730Client } from '@sequencehq/api/dashboard/v20240730'
import { FormFields, useForm } from '@sequencehq/utils'
import { MINUTES } from '@sequencehq/utils/dates'
import { required } from '@sequencehq/utils/validation'
import deepmerge from 'deepmerge'
import { useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import { isOAuthAuthorizedReturn } from 'Integrations/hooks/oauthRedirect.constants'
import { useFeatureFlags } from 'hooks/useFeatureFlags'
import sequenceLogo from 'modules/Integrations/integrationsConfig/common/sequenceLogo.svg'
import { DEFAULT_SETTINGS } from 'modules/Integrations/integrationsConfig/hubSpot/hubSpot.constants'
import { HubSpotConfig } from 'modules/Integrations/integrationsConfig/hubSpot/hubspot.integration.config'
import { disambiguatingPipelineLabel } from 'modules/Integrations/integrationsConfig/hubSpot/pipelineStageLabels'
import { ConfigurationManagementProps } from 'Integrations/domain'

type Option = {
  value: string
  label: string
  secondaryLabel?: string
}

type UseHubSpotConfigurationManagement = (
  props: ConfigurationManagementProps
) => {
  loading: boolean
  showSpinner: boolean
  showNotConnected: boolean
  showSettings: boolean
  fieldsConfig: {
    importCustomerWorkflow: FormFields<HubSpotConfig>['importCustomerWorkflow'] & {
      logoUrls: {
        from: string
        to: string
      }
      status: 'ACTIVE' | 'INACTIVE'
    }
    customerImportStages: FormFields<HubSpotConfig>['customerImportStages'] & {
      options: Option[]
    }
    signedQuoteStage: FormFields<HubSpotConfig>['signedQuoteStage'] & {
      options: Option[]
    }
    pdfSyncOnSign: FormFields<HubSpotConfig>['pdfSyncOnSign']
    dealAmountSync: FormFields<HubSpotConfig>['dealAmountSync']
  }
}

const EMPTY_CONFIG: HubSpotConfig = {
  importCustomerWorkflow: false,
  ...DEFAULT_SETTINGS,
  customerImportStages: [...DEFAULT_SETTINGS.customerImportStages],
  isV2Integration: false
}

export const useHubSpotConfigurationManagement: UseHubSpotConfigurationManagement =
  props => {
    const [searchParams] = useSearchParams()
    const { hubspotV2IntegrationEnabled } = useFeatureFlags()

    const integrationQuery = useQuery(
      dashboard20240730Client.getIntegration,
      'HubSpot',
      {
        staleTime: 1 * MINUTES,
        enabled: props.integration.hasWorkingConnection
      }
    )

    const stageOptions = useMemo(() => {
      const integration =
        integrationQuery.data?.service === 'HubSpot'
          ? integrationQuery.data
          : null

      const stages = (integration?.pipelineStages ?? []).filter(
        stage => !stage.archived
      )
      const pipelineLabelFor = disambiguatingPipelineLabel(stages)

      return stages.map(stage => ({
        value: stage.id,
        label: stage.label,
        secondaryLabel: pipelineLabelFor(stage)
      }))
    }, [integrationQuery.data])

    /**
     * For HubSpot, `CONNECTED` is what puts it in the active list. OAuth
     * returns here before that lands, flashing up the v1 card.
     */
    const isActive = props.integration.state === 'CONNECTED'

    /**
     * Only v2 returns here mid-connection; v1 connects through
     * integration.app, so there is no redirect of ours to wait on.
     */
    const isAwaitingConnection =
      hubspotV2IntegrationEnabled && isOAuthAuthorizedReturn(searchParams)

    const showSettings = Boolean(
      props.integration.configuration?.isV2Integration
    )

    const formValue = (props.integration.configuration ??
      EMPTY_CONFIG) as HubSpotConfig

    const { fields } = useForm({
      value: formValue,
      showValidationErrors: props.showValidationErrors,
      fieldConfiguration: [
        {
          property: 'importCustomerWorkflow'
        },
        {
          property: 'customerImportStages'
        },
        {
          property: 'signedQuoteStage',
          validation: showSettings ? [required] : []
        },
        {
          property: 'pdfSyncOnSign'
        },
        {
          property: 'dealAmountSync'
        }
      ],
      onChange: newData => {
        props.updateConfiguration(newData)
      },
      onValidationStateChange: isValid => {
        props.updateValidationState(isValid)
      }
    })

    const enhancedFields = useMemo(() => {
      return deepmerge(fields, {
        importCustomerWorkflow: {
          logoUrls: {
            from: props.integration.smallLogoUrl,
            to: sequenceLogo
          },
          status: props.integration.configuration?.importCustomerWorkflow
            ? ('ACTIVE' as const)
            : ('INACTIVE' as const)
        },
        customerImportStages: {
          options: stageOptions
        },
        signedQuoteStage: {
          options: stageOptions
        }
      })
    }, [fields, props.integration, stageOptions])

    return {
      loading: integrationQuery.isLoading,
      showSpinner: !isActive && isAwaitingConnection,
      showNotConnected: !isActive && !isAwaitingConnection,
      showSettings,
      fieldsConfig: enhancedFields
    }
  }
